import * as THREE from 'three'
import { DEFAULT_AIM } from '../shared/Targeting'
import { LayerOrders } from '../shared/shared'

const DIR_VECTORS = {
  north: { x: 0, y: 1 },
  south: { x: 0, y: -1 },
  east: { x: 1, y: 0 },
  west: { x: -1, y: 0 },
}

function resolveDir(direction) {
  if (typeof direction === 'string') return DIR_VECTORS[direction] || DIR_VECTORS.south
  return direction
}

const CIRCLE_SEGMENTS = 48
const RENDER_ORDER = LayerOrders.TARGETING
const Z = LayerOrders.TARGETING

const FILL_MAT = new THREE.MeshBasicMaterial({
  color: 0xffffff,
  transparent: true,
  opacity: 0.1,
  side: THREE.DoubleSide,
  depthTest: true,
  depthWrite: false,
})

export class AimVisual {
  constructor(scene) {
    this.scene = scene

    this.trapGeo = new THREE.BufferGeometry()
    this.trapPositions = new Float32Array(6 * 3)
    this.trapGeo.setAttribute('position', new THREE.BufferAttribute(this.trapPositions, 3))
    this.trapMesh = new THREE.Mesh(this.trapGeo, FILL_MAT)
    this.trapMesh.renderOrder = RENDER_ORDER
    this.trapMesh.frustumCulled = false
    this.trapMesh.visible = false
    this.scene.add(this.trapMesh)

    this.circleGeo = new THREE.BufferGeometry()
    this.circlePositions = new Float32Array(CIRCLE_SEGMENTS * 3 * 3)
    this.circleGeo.setAttribute('position', new THREE.BufferAttribute(this.circlePositions, 3))
    this.circleMesh = new THREE.Mesh(this.circleGeo, FILL_MAT)
    this.circleMesh.renderOrder = RENDER_ORDER
    this.circleMesh.frustumCulled = false
    this.circleMesh.visible = false
    this.scene.add(this.circleMesh)
  }

  update({ x, y, direction, attack, aimX, aimY }) {
    const t = attack?.aim
    const shape = t?.shape
    this.trapMesh.visible = false
    this.circleMesh.visible = false
    if (shape === 'trap') {
      this._drawTrap(x, y, direction, t || DEFAULT_AIM)
    } else if (shape === 'circle_self') {
      this._drawCircle(x, y, t?.radius || 1.5)
    } else if (shape === 'circle_aimed') {
      this._drawCircle(aimX ?? x, aimY ?? y, t?.radius || 1.5)
    }
  }

  clear() {
    this.trapMesh.visible = false
    this.circleMesh.visible = false
  }

  _drawTrap(x, y, direction, targeting) {
    const d = resolveDir(direction)
    const perp = { x: -d.y, y: d.x }

    const nearDist = targeting.nearDist ?? -0.2
    const farDist = targeting.farDist ?? 0.8
    const nearHalf = (targeting.nearWidth ?? 0.4) / 2
    const farHalf = (targeting.farWidth ?? 1.2) / 2

    const corners = [
      { x: x + d.x * nearDist - perp.x * nearHalf, y: y + d.y * nearDist - perp.y * nearHalf },
      { x: x + d.x * nearDist + perp.x * nearHalf, y: y + d.y * nearDist + perp.y * nearHalf },
      { x: x + d.x * farDist + perp.x * farHalf,   y: y + d.y * farDist + perp.y * farHalf },
      { x: x + d.x * farDist - perp.x * farHalf,   y: y + d.y * farDist - perp.y * farHalf },
    ]

    const pos = this.trapPositions
    let vi = 0
    pos[vi++] = corners[0].x; pos[vi++] = corners[0].y; pos[vi++] = Z
    pos[vi++] = corners[1].x; pos[vi++] = corners[1].y; pos[vi++] = Z
    pos[vi++] = corners[2].x; pos[vi++] = corners[2].y; pos[vi++] = Z
    pos[vi++] = corners[0].x; pos[vi++] = corners[0].y; pos[vi++] = Z
    pos[vi++] = corners[2].x; pos[vi++] = corners[2].y; pos[vi++] = Z
    pos[vi++] = corners[3].x; pos[vi++] = corners[3].y; pos[vi++] = Z

    this.trapGeo.setDrawRange(0, 6)
    this.trapGeo.attributes.position.needsUpdate = true
    this.trapMesh.visible = true
  }

  _drawCircle(x, y, radius) {
    const pos = this.circlePositions
    let vi = 0
    for (let i = 0; i < CIRCLE_SEGMENTS; i++) {
      const a0 = (i / CIRCLE_SEGMENTS) * Math.PI * 2
      const a1 = ((i + 1) / CIRCLE_SEGMENTS) * Math.PI * 2
      pos[vi++] = x; pos[vi++] = y; pos[vi++] = Z
      pos[vi++] = x + Math.cos(a0) * radius; pos[vi++] = y + Math.sin(a0) * radius; pos[vi++] = Z
      pos[vi++] = x + Math.cos(a1) * radius; pos[vi++] = y + Math.sin(a1) * radius; pos[vi++] = Z
    }
    this.circleGeo.setDrawRange(0, CIRCLE_SEGMENTS * 3)
    this.circleGeo.attributes.position.needsUpdate = true
    this.circleMesh.visible = true
  }

  destroy() {
    this.scene.remove(this.trapMesh)
    this.scene.remove(this.circleMesh)
    this.trapGeo.dispose()
    this.circleGeo.dispose()
  }
}
